import React, { memo } from 'react';
import compact from 'lodash/compact';
import get from 'lodash/get';
import PropTypes from 'prop-types';
import Autocomplete from '@mui/material/Autocomplete';
import { styled } from '@mui/material/styles';
import InputLabel from '@mui/material/InputLabel';
import TextField from '@mui/material/TextField';
import { Colors } from 'modules/theme';
import AvatarOptionUI from '../Option/AvatarOptionUI/AvatarOptionUI';

const LabelRequired = styled('span')({
  fontWeight: 600,
  fontSize: '13px',
  color: Colors.grayishBlue,
  textTransform: 'none',
});

const StyledInputLabel = styled(InputLabel)({
  color: Colors.darkGrey,
  fontWeight: 500,
  fontSize: '16px',
  lineHeight: '14px',
  letterSpacing: '0.5px',
  fontFamily: 'Inter, sans-serif',
  textTransform: 'uppercase',
});

const StyledTextField = styled(TextField)({
  color: Colors.darkBlue,
  fontSize: '13px',
  fontWeight: 500,
  lineHeight: '24px',
  fontFamily: 'Inter, sans-serif',
  minHeight: '40px',
  border: `1px solid ${Colors.inputBorder}`,
  backgroundColor: Colors.inputBackground,
  borderRadius: '6px',
  width: 'stretch',
  '& .MuiOutlinedInput-notchedOutline': {
    border: 'none',
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    border: 'none',
  },
  '& .Mui-focused .MuiOutlinedInput-notchedOutline': {
    border: 'none',
  },
});

function ListSelectInput({
  name,
  value,
  loading,
  onInputChange,
  options,
  onChange,
  disabled,
  required,
  isMultiSelect,
  label,
  error,
  placeholder,
}) {
  function renderOption(props, userOption, { selected }) {
    const locationDescriptionArray = [
      get(userOption, 'city'),
      get(userOption, 'state.name'),
      get(userOption, 'country.name'),
    ];

    return (
      <AvatarOptionUI
        divider
        isCheckbox={!!isMultiSelect}
        key={`${name}-${userOption.value}`}
        label={userOption.label}
        selected={selected}
        containerStyle={{ padding: '5px 0', margin: '5px 15px' }}
        title={userOption.label}
        description={compact(locationDescriptionArray).join(', ')}
        avatarUrl={userOption.profileImage}
        {...props}
      />
    );
  }

  return (
    <div>
      <Autocomplete
        multiple={!!isMultiSelect}
        getOptionLabel={option => (option.label ? option.label : '')}
        disabled={disabled}
        loading={loading}
        id="tags-standard"
        options={options}
        value={value}
        onInputChange={onInputChange}
        onChange={onChange}
        isOptionEqualToValue={(option, value) => value.id === option.id}
        renderInput={params => (
          <>
            <StyledInputLabel
              error={error}
              shrink
              htmlFor="custom-css-standard-input"
            >
              {label}{' '}
              {required && (
                <LabelRequired style={error ? { color: Colors.error } : {}}>
                  {' '}
                  (required)
                </LabelRequired>
              )}
            </StyledInputLabel>
            <StyledTextField
              {...params}
              required
              error={error}
              helperText={error}
              id="custom-css-standard-input"
              variant="outlined"
              placeholder={placeholder || 'Enter recipients name'}
            />
          </>
        )}
        renderOption={renderOption}
      />
    </div>
  );
}

ListSelectInput.propTypes = {
  name: PropTypes.string,
  label: PropTypes.string,
  disabled: PropTypes.bool,
  isMultiSelect: PropTypes.bool,
  required: PropTypes.bool,
  onChange: PropTypes.func,
  placeholder: PropTypes.string,
  error: PropTypes.bool,
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.array,
    PropTypes.number,
    PropTypes.object,
  ]),
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      label: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    }),
  ),
};

export default memo(ListSelectInput);
